import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Auth } from '@angular/fire/auth';
import { firstValueFrom, of } from 'rxjs';
import { UsersService } from '../users.service';
import { OperatorsService } from '../operators.service';
import { User } from '../user.model';
import { OrderListComponent } from '../../orders/order-list/order-list.component';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { AvatarPickerComponent } from '../../../shared/components/avatar-picker/avatar-picker.component';
import { AvatarService } from '../../../shared/services/avatar.service';

@Component({
  selector: 'app-user-details',
  imports: [ReactiveFormsModule, OrderListComponent, ConfirmDialogComponent, AvatarComponent, AvatarPickerComponent],
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDetailsComponent {
  readonly #router = inject(Router);
  readonly #browserLocation = inject(Location);
  readonly #usersService = inject(UsersService);
  readonly #operatorsService = inject(OperatorsService);
  readonly #avatarService = inject(AvatarService);

  readonly #userId = inject(ActivatedRoute).snapshot.paramMap.get('id');

  /** Live users/{id} document, so saved changes show up without reloading. */
  protected readonly user = toSignal<User | undefined>(
    this.#userId ? this.#usersService.getUser(this.#userId) : of(undefined),
  );

  // --- Edit: avatar and display name ---

  protected readonly isEditing = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  /** Chosen in the picker; uploaded on Save. */
  protected readonly avatarFile = signal<File | null>(null);
  protected readonly nameControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(/\S/)], // not only spaces
  });

  protected startEdit() {
    this.nameControl.reset(this.user()?.displayName ?? '');
    this.avatarFile.set(null);
    this.errorMessage.set(null);
    this.isEditing.set(true);
  }

  protected cancelEdit() {
    this.avatarFile.set(null);
    this.errorMessage.set(null);
    this.isEditing.set(false);
  }

  /** The user document exists, so the avatar is uploaded first and everything is saved in one write. */
  protected async save() {
    const user = this.user();
    if (!user || this.nameControl.invalid || this.isSaving()) {
      this.nameControl.markAsTouched();
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set(null);
    try {
      const file = this.avatarFile();
      const changes: Partial<User> = {
        displayName: this.nameControl.value.trim(),
        ...(file && (await this.#avatarService.upload(`users/${user.id}`, file))),
      };
      await firstValueFrom(this.#usersService.updateUser(user.id, changes));
      this.cancelEdit();
    } catch (error) {
      console.error('Error saving user:', error);
      this.errorMessage.set('Failed to save. Please try again.');
    } finally {
      this.isSaving.set(false);
    }
  }

  // --- Delete operator (admins only; the deleteOperator function checks this too) ---

  readonly #isAdmin = signal(false);
  protected readonly canDelete = computed(() => this.#isAdmin() && this.user()?.role === 'operator');
  protected readonly confirmingDelete = signal(false);
  protected readonly deleting = signal(false);
  protected readonly deleteError = signal<string | null>(null);

  constructor() {
    inject(Auth).currentUser?.getIdTokenResult()
      .then((result) => this.#isAdmin.set(result.claims['role'] === 'admin'))
      .catch(() => this.#isAdmin.set(false));
  }

  protected askDelete() {
    this.deleteError.set(null);
    this.confirmingDelete.set(true);
  }

  protected cancelDelete() {
    if (!this.deleting()) {
      this.confirmingDelete.set(false);
    }
  }

  protected async confirmDelete() {
    const user = this.user();
    if (!user || this.deleting()) return;

    this.deleting.set(true);
    this.deleteError.set(null);
    const result = await this.#operatorsService.deleteOperator(user.id);
    this.deleting.set(false);

    if (result.success) {
      this.confirmingDelete.set(false);
      this.#router.navigate(['/main/operators']);
    } else {
      this.deleteError.set(result.error ?? 'Failed to delete operator');
    }
  }

  protected goBack() {
    this.#browserLocation.back();
  }
}
